import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, errMsg } from "../../lib/api";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { Icon } from "../ui/Icon";
import { Badge } from "../ui/Badge";
import { Banner } from "../ui/Banner";
import { Field } from "../ui/Field";
import { Toggle } from "../ui/Toggle";

type Reason = {
  id: number;
  key: string;
  label_it: string;
  requires_note: boolean;
  note_label: string | null;
  is_manual: boolean;
  active: boolean;
  sort_order: number;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

function slugify(label: string): string {
  return label
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
}

export function StockOutReasonsAdmin({ open, onClose }: Props) {
  const qc = useQueryClient();
  const [mode, setMode] = useState<"list" | "create">("list");
  const [label, setLabel] = useState("");
  const [requiresNote, setRequiresNote] = useState(false);
  const [noteLabel, setNoteLabel] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { data: reasons, isLoading } = useQuery<Reason[]>({
    queryKey: ["stock-out-reasons", "admin"],
    queryFn: async () =>
      (await api.get("/api/stock-out-reasons", { params: { include_inactive: true } })).data,
    enabled: open,
  });

  useEffect(() => {
    if (!open) return;
    setMode("list");
    setLabel("");
    setRequiresNote(false);
    setNoteLabel("");
    setError(null);
  }, [open]);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["stock-out-reasons"] });
    qc.invalidateQueries({ queryKey: ["stock-out-reasons", "admin"] });
  };

  const createMut = useMutation({
    mutationFn: async () =>
      (
        await api.post("/api/stock-out-reasons", {
          key: slugify(label),
          label_it: label.trim(),
          requires_note: requiresNote,
          note_label: requiresNote ? noteLabel.trim() || null : null,
        })
      ).data,
    onSuccess: () => {
      invalidate();
      setMode("list");
      setLabel("");
      setRequiresNote(false);
      setNoteLabel("");
    },
    onError: (e) => setError(errMsg(e)),
  });

  const toggleMut = useMutation({
    mutationFn: async (v: { id: number; active: boolean }) =>
      (await api.patch(`/api/stock-out-reasons/${v.id}`, { active: v.active })).data,
    onSuccess: () => invalidate(),
    onError: (e) => setError(errMsg(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (id: number) => (await api.delete(`/api/stock-out-reasons/${id}`)).data,
    onSuccess: () => invalidate(),
    onError: (e) => setError(errMsg(e)),
  });

  const busy = createMut.isPending || toggleMut.isPending || deleteMut.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === "create" ? "Nuovo motivo di uscita" : "Motivi di uscita"}
      footer={
        mode === "create" ? (
          <>
            <Button variant="ghost" block onClick={() => setMode("list")} disabled={busy}>
              Annulla
            </Button>
            <Button
              variant="primary"
              block
              disabled={busy || label.trim().length < 2 || (requiresNote && noteLabel.trim().length < 1)}
              onClick={() => {
                setError(null);
                createMut.mutate();
              }}
            >
              Crea motivo
            </Button>
          </>
        ) : (
          <Button variant="primary" block onClick={() => setMode("create")}>
            <Icon name="plus" size={14} /> Nuovo motivo custom
          </Button>
        )
      }
    >
      {error && (
        <Banner tone="danger" icon="info" style={{ marginBottom: 12 }}>
          {error}
        </Banner>
      )}

      {mode === "create" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <Field label="Etichetta motivo" hint="Come apparirà nella tendina Stock Out">
            <input
              className="tcg-input"
              value={label}
              autoFocus
              placeholder="es. Reso fornitore"
              onChange={(e) => setLabel(e.target.value)}
            />
          </Field>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "10px 2px",
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700 }}>Richiede una nota obbligatoria</div>
            <Toggle on={requiresNote} onChange={setRequiresNote} />
          </div>
          {requiresNote && (
            <Field label="Etichetta nota" hint="es. Nome, Info, Riferimento">
              <input
                className="tcg-input"
                value={noteLabel}
                placeholder="Nome"
                onChange={(e) => setNoteLabel(e.target.value)}
              />
            </Field>
          )}
        </div>
      )}

      {mode === "list" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {isLoading && <div style={{ color: "var(--tcg-muted)", fontSize: 13 }}>Caricamento…</div>}
          {reasons?.map((r) => (
            <div
              key={r.id}
              style={{
                border: "1px solid var(--tcg-border)",
                borderRadius: 14,
                padding: "10px 12px",
                background: r.active ? "var(--tcg-card)" : "var(--tcg-bg)",
                opacity: r.active ? 1 : 0.6,
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 800, color: "var(--tcg-ink)" }}>
                    {r.label_it}
                  </div>
                  <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                    <span className="tcg-mono" style={{ fontSize: 9.5, color: "var(--tcg-muted)" }}>
                      {r.key}
                    </span>
                    {r.requires_note && (
                      <Badge tone="warn">nota: {r.note_label || "richiesta"}</Badge>
                    )}
                    {!r.is_manual && <Badge tone="ghost">default</Badge>}
                  </div>
                </div>
                <Toggle
                  on={r.active}
                  onChange={(v) => {
                    setError(null);
                    toggleMut.mutate({ id: r.id, active: v });
                  }}
                />
                {r.is_manual ? (
                  <Button
                    variant="danger"
                    size="small"
                    disabled={busy}
                    onClick={() => {
                      setError(null);
                      if (window.confirm(`Eliminare il motivo "${r.label_it}"?`)) deleteMut.mutate(r.id);
                    }}
                  >
                    <Icon name="x" size={12} />
                  </Button>
                ) : (
                  <span
                    title="Motivo di default: non eliminabile"
                    style={{ color: "var(--tcg-muted)", padding: "0 6px" }}
                  >
                    <Icon name="lock" size={14} />
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
